<template>
  <div class="custom-sound-wave">
    <template v-if="$props.template && $props.template.color_picker">
      <div class="customization-title">
        {{ $props.template.title }}
      </div>
      <ColorPicker
        @change-colors="changeColors"
        :value="$props.template.values[0]"
        :type="$props.template.color_picker"
        :id="'soundWave'"
      />
    </template>

    <template v-if="$props.barThickness">
      <div class="customization-title mt-sm">
        {{ $props.barThickness.title }}
      </div>
      <RangeSlider
        class="mt-xs"
        @change-range="updateBarThickness"
        :min="$props.barThickness.min || MIN_BAR_THICKNESS"
        :max="$props.barThickness.max || MAX_BAR_THICKNESS"
        :value="barThickness"
      ></RangeSlider>
    </template>

    <template v-if="$props.spaceBetweenBar">
      <div class="customization-title mt-sm">
        {{ $props.spaceBetweenBar.title }}
      </div>
      <RangeSlider
        class="mt-xs"
        @change-range="updateSpaceBetweenBars"
        :min="$props.spaceBetweenBar.min || MIN_SPACE_BETWEEN_BAR"
        :max="$props.spaceBetweenBar.max || MAX_SPACE_BETWEEN_BAR"
        :value="spaceBetweenBars"
      ></RangeSlider>
    </template>

    <template v-if="$props.barHeight">
      <div class="customization-title mt-sm">
        {{ $props.barHeight.title }}
      </div>
      <RangeSlider
        class="mt-xs"
        @change-range="updateBarHeight"
        :min="$props.barHeight.min || MIN_BAR_THICKNESS"
        :max="$props.barHeight.max || MAX_BAR_THICKNESS"
        :value="barHeight"
      ></RangeSlider>
    </template>

    <SoundWave
      v-bind="{
        width: soundWaveWidth,
        height: soundWaveHeight,
        barThickness,
        spaceBetweenBars,
        barHeight,
        colors,
        waveForm,
      }"
      @change-image-sound-wave="$emit('change-image-sound-wave', $event)"
    />
  </div>
</template>
<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import SoundWave from './SoundWave.vue'
import RangeSlider from '@/ui-kits/RangeSlider.vue'
import ColorPicker from '@/ui-kits/ColorPicker.vue'
import EventBus from '@/services/eventbus'
import {
  EVENT_CHANGE_AUDIO_SOUND_WAVE,
  EVENT_CHANGE_SPOTIFY_SOUND_WAVE,
} from '@/utils'

const props = withDefaults(
  defineProps<{
    barThickness?: { title: string; min: number; max: number }
    spaceBetweenBar?: { title: string; min: number; max: number }
    barHeight?: { title: string; min: number; max: number }
    soundWaveWidth?: number
    soundWaveHeight?: number
    template?: { title: string; values: string[]; color_picker: string }
  }>(),
  {}
)

const BACKGROUND_COLOR_DEFAULT = 'black'
const MIN_BAR_THICKNESS = 1
const MAX_BAR_THICKNESS = 16
const MIN_SPACE_BETWEEN_BAR = 0
const MAX_SPACE_BETWEEN_BAR = 16
const MIN_BAR_HEIGHT = -100
const MAX_BAR_HEIGHT = 100
const colors = ref([BACKGROUND_COLOR_DEFAULT])
const barThickness = ref(1)
const spaceBetweenBars = ref(1)
const barHeight = ref(0)
const waveForm = ref([])
const waveFormOriginal = ref([])

onMounted(() => {
  if (props.template?.values?.length) {
    colors.value = props.template.values[0].split(',')
  } else {
    colors.value = [BACKGROUND_COLOR_DEFAULT]
  }
  EventBus.on(EVENT_CHANGE_AUDIO_SOUND_WAVE, (data) => {
    waveForm.value = data
    waveFormOriginal.value = JSON.parse(JSON.stringify(data))
  })
  EventBus.on(EVENT_CHANGE_SPOTIFY_SOUND_WAVE, (data) => {
    waveForm.value = data
    waveFormOriginal.value = JSON.parse(JSON.stringify(data))
  })
})

function updateBarThickness(range: number) {
  if (
    range >= (props.barThickness?.max || MAX_BAR_THICKNESS) ||
    range < (props.barThickness?.min || MIN_BAR_THICKNESS)
  )
    return
  barThickness.value = range
}

function updateSpaceBetweenBars(range: number) {
  if (
    range >= (props.spaceBetweenBar?.max || MAX_SPACE_BETWEEN_BAR) ||
    range < (props.spaceBetweenBar?.min || MIN_SPACE_BETWEEN_BAR)
  )
    return
  spaceBetweenBars.value = range
}

function updateBarHeight(range: number) {
  if (
    range >= (props.barHeight?.max || MAX_BAR_HEIGHT) ||
    range < (props.barHeight?.min || MIN_BAR_HEIGHT)
  )
    return
  barHeight.value = range
  updateWaveFormByBarHeight(range)
}

function updateWaveFormByBarHeight(barHeight: number) {
  if (waveForm.value.length) {
    waveForm.value = []
    waveForm.value = JSON.parse(JSON.stringify(waveFormOriginal.value)).map(
      (value: number) => {
        if (barHeight > 0) {
          const number =
            value + barHeight / (props.barHeight?.max || MAX_BAR_HEIGHT)
          return number > 1 ? 2 - number : number
        } else {
          const number =
            value +
            barHeight / (Math.abs(props.barHeight?.min || 0) || MIN_BAR_HEIGHT)
          return number < 0 ? number + 1 : number
        }
      }
    )
  }
}

function changeColors(listColor: string[]) {
  if (listColor?.length) {
    colors.value = listColor
  } else {
    colors.value = [BACKGROUND_COLOR_DEFAULT]
  }
}
</script>
